/**
 * Shared full-screen loading spinner. Extracted from AppRouter.tsx so the
 * auth transition and the ShopDataGate render identical visuals.
 */

import { FC, ReactNode } from "react";
import { Button, Spin } from "antd";

interface FullScreenSpinnerProps {
  /** Optional message under the spinner. Defaults to "Loading...". */
  message?: string;
  /** Optional retry / reload affordance. */
  showRetry?: boolean;
  onRetry?: () => void;
  retryLabel?: string;
  /** Optional secondary line, rendered above the retry button. */
  retryPrompt?: ReactNode;
}

const FullScreenSpinner: FC<FullScreenSpinnerProps> = ({
  message = "Loading...",
  showRetry = false,
  onRetry,
  retryLabel = "Reload",
  retryPrompt = "Taking longer?",
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      gap: "1rem",
    }}
  >
    <Spin size="large" />
    <span
      style={{
        fontSize: "1rem",
        color: "#666",
        animation: "heartbeat 1.5s ease-in-out infinite",
      }}
    >
      {message}
    </span>
    {showRetry && (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.75rem",
          marginTop: "1rem",
          animation: "fadeSlideIn 0.5s ease-out forwards",
        }}
      >
        <span
          style={{
            fontSize: "1.125rem",
            color: "#595959",
            fontWeight: 500,
          }}
        >
          {retryPrompt}
        </span>
        <Button type="primary" size="large" onClick={onRetry}>
          {retryLabel}
        </Button>
      </div>
    )}
    <style>
      {`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.7; }
        }
        @keyframes fadeSlideIn {
          0% { opacity: 0; transform: translateY(-0.625rem); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}
    </style>
  </div>
);

export default FullScreenSpinner;
